import { SimulatePriceRequest, SimulatePriceResponse } from 'features/simulation/types'
import { createURIEncodedDataString } from 'utils/helpers'
import { Subscribe } from 'types/subscribe'
import { TownResponse } from 'types/towns'
import { CheckPromoReferralCodeResponse, GetReferralCodeResponse } from 'types/promo-referral-codes'
import request, { getAxiosRequestConfig } from 'utils/request'
import { ContactMeTicket } from 'types/contact-me-ticket'
import { AllProducts } from 'types/product-data'
import { CreateCustomer } from 'features/registration/types'
import { GetDataByOpportunityIdResponse } from 'types/request'

/**
 * Check if the given code is a valid promo or referral through an API call to SF
 *
 * @param code
 * @return Promise<CheckPromoReferralCodeResponse>
 */
export const checkPromoReferralCode = async (code: string): Promise<CheckPromoReferralCodeResponse> => {
  const response = await request(getAxiosRequestConfig('GET', `website/promo-referral-code?code=${code}`))

  return response.success ? response.data : { valid: false }
}

/**
 * Creates a contact me ticket through the API
 *
 * @param data
 * @returns Promise<{ success: boolean }>
 */
export async function createContactMeTicket(data: ContactMeTicket): Promise<{ success: boolean }> {
  const { success } = await request(getAxiosRequestConfig('POST', 'website/contact', data))

  return { success }
}

/**
 * Creates a customer in Salesforce
 *
 * @param data
 * @return Promise<{ success: boolean }>
 */
export const createCustomer = async (data: CreateCustomer): Promise<{ success: boolean }> => {
  const { success } = await request(getAxiosRequestConfig('POST', 'customers', data))

  return { success }
}

/**
 * Fetches the customer data based on the opportunity id
 *
 * @param opportunityId
 * @returns Promise<GetDataByOpportunityIdResponse>
 */
export const fetchDataByOpportunityId = async (opportunityId: string): Promise<GetDataByOpportunityIdResponse> => {
  const response = await request(getAxiosRequestConfig('GET', `website/customer-data?opportunityId=${opportunityId}`))

  return response.success ? response.data : null
}

/**
 * Fetches the pricelists for all products
 *
 * @returns Promise<AllProducts|null>
 */
export const fetchProducts = async (): Promise<AllProducts | null> => {
  const response = await request(getAxiosRequestConfig('GET', 'products'))

  return response.success ? response.data : null
}

/**
 * Fetches the price simulation from the API
 *
 * @param payload
 * @return Promise<SimulatePriceResponse>
 */
export const fetchPriceSimulation = async (data: SimulatePriceRequest): Promise<SimulatePriceResponse> => {
  const response = await request(getAxiosRequestConfig('POST', 'website/price-simulation', data))

  return response.data
}

/**
 * Gets the referral code for a customer by a given email address
 *
 * @param email
 * @return Promise<GetReferralCodeResponse>
 */
export const getReferralCodeByEmail = async (email: string): Promise<GetReferralCodeResponse> => {
  const response = await request(getAxiosRequestConfig('GET', `website/referral-code?email=${email}`))

  return response.success ? response.data : { found: false }
}

/**
 * Gets the simulation parameters for a given opportunityId
 *
 * @param opportunityId
 * @returns Promise<string>
 */
export const fetchSimulationParameters = async (opportunityId: string): Promise<string> => {
  const { success, data } = await request(getAxiosRequestConfig('GET', `website/simulation-parameters?opportunityId=${opportunityId}`))

  return success ? data : null
}

/**
 * Fetch the town from the API based on the given postal code
 *
 * @param postalCode
 * @return Promise<TownResponse>
 */
export const getTown = async (postalCode: number): Promise<TownResponse> => {
  const response = await request(getAxiosRequestConfig('GET', `towns/${postalCode}`))

  return response.success ? response.data : null
}

/**
 * Checks if a given email is already registered as a customer in Salesforce
 *
 * @param email
 * @return Promise<boolean>
 */
export const isCustomer = async (email: string): Promise<boolean> => {
  const response = await request(getAxiosRequestConfig('GET', `customer/exists?email=${email.toLowerCase()}`))

  return response.success
}

/**
 * Subscribes a lead in Salesforce
 *
 * @param data
 * @return Promise<string>
 */
export const subscribe = async (data: Subscribe): Promise<string> => {
  // Subscribe the prospect in Pardot through form handler link if the form handler property has been set
  if (data.pardotFormHandler !== undefined) {
    const formData = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      language: data.language,
      emailOptIn: 'true'
    }

    if (process.env.NEXT_PUBLIC_VERCEL_ENV === 'production') subscribePardotProspect(data.pardotFormHandler, formData)
  }

  // Remove pardotFormHandler from data (not needed in API for subscribe)
  const { pardotFormHandler, ...dataWithoutPardotFormHandler } = data

  // Subscribe the prospect in Salesforce through the API
  const response = await request(getAxiosRequestConfig('POST', 'customers/subscribe', dataWithoutPardotFormHandler))

  return response.success ? response.data.opportunityId : ''
}

/**
 * Subscribes a pardot prospect
 *
 * @param formHandlerCode
 * @param formData
 * @return Promise<Response>
 */
export const subscribePardotProspect = async (formHandlerCode: string, formData: Record<string, any>): Promise<Response> => {
  return await fetch('https://www2.boltenergie.be/l/877822/' + formHandlerCode, {
    method: 'POST',
    mode: 'no-cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
    },
    body: createURIEncodedDataString(formData)
  })
}
